/* KANON `Detal klienta v3`, variant a — the shell: two fixed bars over one scrolling body.
 *
 * ── Fit-or-scroll, and the whole screen rests on one rule ─────────────────────────────────────
 * `.page` is `min-height: 0` inside the shell's flex column, `.body` takes the remainder, and BOTH
 * scrollports — the summary column and the tab panel — scroll independently. A missing
 * `min-height: 0` on a flex row makes its children shrink under their content instead of
 * overflowing, which deletes the scroller entirely; that is a failure this app has already paid for
 * once, and it is why the property appears three times below rather than once.
 *
 * ── And `max-height: 100dvh` is what makes any of that true ───────────────────────────────────
 * `min-height: 0` bounds nothing on its own. The shell is `min-height: 100dvh` on a MIN, so it
 * grows to whatever its content asks for, and a `flex: 1` child of an auto-height parent resolves
 * to its content too. Measured at 1280×620 on the Pomiary tab: the document was 1012px tall,
 * NEITHER scrollport engaged, and the whole page scrolled instead — carrying the tab bar off the
 * top, so the screen's own navigation was the first thing to disappear. Capping the page is what
 * hands the overflow back to the two boxes that are supposed to own it. */

.page {
  position: relative;
  display: flex;
  max-height: 100dvh;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  background: var(--bg);
}

/* Where the session overlay sits — over the BODY and not over the two bars above it. They stay lit
 * and usable, so `‹ Klienci` is reachable with a session open; only the panels under them are
 * covered. The offset is the two bars' own height tokens rather than a copy of their numbers, so
 * the three cannot drift. */
.overlayHost {
  position: absolute;
  inset: var(--h-detail-chrome) 0 0;
}

/* ── the identity bar ─────────────────────────────────────────────────────────────────────── */

.topBar {
  display: flex;
  flex: none;
  gap: var(--space-10px);
  align-items: center;
  height: var(--h-detail-crumb);
  padding: 0 var(--space-30px);
  background: var(--bg-card);
  border-bottom: var(--border-hairline) solid var(--border);
}

.crumbs {
  display: flex;
  flex: none;
  gap: var(--space-10px);
  align-items: center;
}

.crumbLink {
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: none;
  border: none;
}

.crumbLink:hover {
  color: var(--text);
}

.crumbLink:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.crumbSep {
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
}

/* The monogram disc and the photo are the same BOX and two different fills of it: the monogram is
 * drawn when the client has no photo, the photo when they have one, and the empty disc when the
 * header read has not answered yet. All three are `--sz-detail-avatar` square from the first frame,
 * which is what keeps the client's name beside them from sliding.
 *
 * The photo's own treatment — the fit and the ground under it — is `shared/ui/photoBox`'s `.photo`,
 * applied as a second class in the TSX. Nothing is declared on both sides, so `background` and
 * `object-fit` are absent from this block. */
.avatar,
.avatarPhoto {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--sz-detail-avatar);
  height: var(--sz-detail-avatar);
  overflow: hidden;
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-circle);
}

/* The monogram's own ground. The photo takes `photoBox`'s. */
.avatar {
  background: var(--bg-sunken);
}

/* The document's only `<h1>`. It lives here rather than in the summary column, which is what lets
 * the column be absent on five tabs out of six without leaving the page unnamed. */
.clientName {
  margin: var(--space-0);
  overflow: hidden;
  font-size: var(--fs-detail-name);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text);
  letter-spacing: var(--ls-name);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.statusPill {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  height: var(--h-detail-pill);
  padding: 0 var(--space-9px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

.statusDot {
  display: block;
  flex: none;
  width: var(--space-6px);
  height: var(--space-6px);
  background: var(--text-3);
  border-radius: var(--r-circle);
}

.statusDotActive {
  background: var(--good);
}

/* `margin-inline-start: auto` pushes whatever follows to the far end of the bar. */
.barSince {
  flex: none;
  margin-inline-start: auto;
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
}

/* ── `‹ Poprzednia / Następna ›` ───────────────────────────────────────────────────────────────
 *
 * The pair lives at the far end of the bar, and WHICH element carries the push depends on whether
 * the since label is drawn: it is absent for a client whose start date the read did not carry. So
 * the nav claims the free space by default, and hands that job back to the label when the label is
 * there — otherwise two `auto` margins would split the gap between them and strand "klient od 14
 * mies." in the middle of the bar. */
.walk {
  display: flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  margin-inline-start: auto;
}

.barSince + .walk {
  margin-inline-start: var(--space-2);
}

/* The geometry is the status pill's, because they sit on the same 52px row and a second height
 * would read as a second kind of thing. Neutral throughout: this screen's accent budget is spent
 * on the selected tab, the section count and the plan card's link. */
.walkStep {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--h-detail-pill);
  padding: 0 var(--space-9px);
  font-family: var(--font-body);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: none;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

.walkStep:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.walkStep:hover:not([aria-disabled='true']) {
  color: var(--text);
}

/* Refused, and still in the tab order — `aria-disabled` rather than `disabled`, because the reason
 * hangs off this element and a `disabled` button takes its own explanation out of reach. */
.walkStep[aria-disabled='true'] {
  color: var(--text-3);
  cursor: not-allowed;
}

/* ── the tab bar ──────────────────────────────────────────────────────────────────────────── */

/* The bar's own box, and nothing else: `shared/ui/TabStrip` owns the strip, the tab, its ink, its
 * weights, its focus ring and its rule, and NOTHING may be declared on both sides — see that
 * stylesheet's header. v3 draws this band as a 46px strip on the card's ground under the client's
 * name. */
.tabBar {
  gap: var(--space-14px);
  height: var(--h-detail-tabbar);
  padding: 0 var(--space-30px);
  background: var(--bg-card);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* One line about the tab in view, published by the tab itself — see `detailTabMeta`. */
.tabMeta {
  flex: none;
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
}

/* The Forma tab's unread dot: a recording or a client comment the trainer has not seen (R9). Inline,
 * so the dot arriving with the movement list never changes the tab's line box. */
.tabWithDot {
  white-space: nowrap;
}

.tabDot {
  display: inline-block;
  width: var(--space-7px);
  height: var(--space-7px);
  margin-inline-start: var(--space-7px);
  vertical-align: middle;
  background: var(--accent);
  border-radius: var(--r-circle);
}

/* ── the body ─────────────────────────────────────────────────────────────────────────────── */

/* v3's split: `20px 30px 24px` around an 18px gap, so the summary column and the tab zone sit on
 * the canvas as two floating panels. The insets are unconditional now — the Kalendarz tab's
 * full-bleed exception is gone, because in v3 the summary column stands on Przegląd alone and the
 * calendar already gets the whole zone. */
.body {
  display: flex;
  flex: 1;
  gap: var(--space-5);
  min-height: 0;
  padding: var(--space-20px) var(--space-30px) var(--space-6);
}

.panel {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

/* The panel is a tab stop (see `ClientDetailScreen`), so it needs the app's ring rather than the
 * browser's blue one. Inset, because the panel fills the zone and a ring pushed outward would sit
 * under the tab bar's own hairline. Drawn on a layer above the content, since a sticky child (the
 * survey stream) would otherwise paint over an inset outline. */
.panel:focus-visible {
  outline: none;
}

.panel:focus-visible::after {
  position: absolute;
  z-index: 1;
  inset: var(--space-0);
  border: var(--border-focus) solid var(--accent);
  content: '';
  pointer-events: none;
}

/* ── the two dead ends ─────────────────────────────────────────────────────── */

.deadEnd {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3);
  align-items: flex-start;
  justify-content: center;
  padding: var(--space-10);
  background: var(--bg);
}

.deadEndTitle {
  margin: 0;
  font-size: var(--fs-h2);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  letter-spacing: var(--ls-title);
}

.deadEndBody {
  max-width: var(--w-dead-end-body);
  margin: 0;
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.deadEndLink {
  padding: 0;
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  color: var(--accent-text);
  cursor: pointer;
  background: none;
  border: none;
}

.deadEndLink:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

/* „Przypisz plan" — the second entry into the assignment dialog, at the END of the identity bar's
 * control row so its arrival moves nothing that was already there. Geometry only: the fill, the
 * white ink and the pressed state are `shared/ui/emberButton`'s. */
.assignPlan {
  flex: none;
  height: var(--hit-target);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);
  border-radius: var(--radius-sm);
}

/* „Plan z Buddym", beside it: the ghost's treatment at the same height, with Buddy's mark standing
 * still. Geometry only, as above. */
.buddyPlan {
  gap: var(--space-2);
  height: var(--hit-target);
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);
  background: transparent;
  border-radius: var(--radius-sm);
}

.buddyPlanMark {
  width: var(--sz-buddy-mark-inline);
  height: var(--sz-buddy-mark-inline);
}
